// SIGNET payment-credentials scheme parser.
// Parses many credential formats into a normalized object:
//   { scheme, recipient, recipient_name, amount, currency, reference, raw }
// Supported: SIGNET-PAY|IBAN|amount|ccy|ref, plain IBAN, EPC/SEPA giro (BCD),
// EMVCo merchant QR (TLV, starts 000201), UPI (upi://pay?...), URLs, raw text.

const IBAN_RE = /([A-Z]{2}\d{2}[A-Z0-9]{10,30})/i;
const clean = (s) => (s || "").trim();
const stripIban = (s) => (s || "").replace(/\s+/g, "").toUpperCase();

// ISO 4217 numeric -> our supported currencies
const NUMERIC_CCY = { "978": "EUR", "840": "USD" };
const normCcy = (c) => {
  const u = (c || "").toString().trim().toUpperCase();
  if (u === "EUR" || u === "USD") return u;
  if (NUMERIC_CCY[u]) return NUMERIC_CCY[u];
  return "EUR";
};

export const buildQrPayload = ({ iban, amount, currency, reference }) => {
  return [
    "SIGNET-PAY",
    stripIban(iban),
    amount != null && amount !== "" ? Number(amount).toFixed(2) : "",
    currency || "",
    reference || "",
  ].join("|");
};

// --- individual format parsers ---
const parseSignet = (t) => {
  const p = t.split("|");
  return {
    scheme: "signet",
    recipient: stripIban(p[1]),
    recipient_name: null,
    amount: p[2] ? parseFloat(p[2]) : null,
    currency: p[3] ? normCcy(p[3]) : null,
    reference: p[4] || "",
  };
};

const parseEpc = (t) => {
  // BCD giro code: newline-separated. Lines: [BCD, ver, charset, id(SCT), BIC, Name, IBAN, EURamount, purpose, ref, remittance]
  const L = t.split(/\r?\n/);
  const amtRaw = (L[7] || "").toUpperCase().replace("EUR", "").trim();
  return {
    scheme: "epc",
    recipient: stripIban(L[6] || ""),
    recipient_name: (L[5] || "").trim() || null,
    amount: amtRaw ? parseFloat(amtRaw) : null,
    currency: "EUR",
    reference: (L[9] || L[10] || "").trim(),
  };
};

const parseUpi = (t) => {
  try {
    const q = new URLSearchParams(t.split("?")[1] || "");
    return {
      scheme: "upi",
      recipient: q.get("pa") || "",
      recipient_name: q.get("pn") || null,
      amount: q.get("am") ? parseFloat(q.get("am")) : null,
      currency: normCcy(q.get("cu") || "EUR"),
      reference: q.get("tn") || "",
    };
  } catch (e) {
    return { scheme: "upi", recipient: t, recipient_name: null, amount: null, currency: "EUR", reference: "" };
  }
};

const parseEmvco = (t) => {
  // Minimal EMVCo TLV walk: ID(2) LEN(2) VALUE(len)
  const out = {};
  let i = 0;
  while (i + 4 <= t.length) {
    const id = t.substr(i, 2);
    const len = parseInt(t.substr(i + 2, 2), 10);
    if (Number.isNaN(len)) break;
    out[id] = t.substr(i + 4, len);
    i += 4 + len;
  }
  // 54 amount, 53 currency(numeric), 59 merchant name, 62 additional (may contain 01/05=ref)
  let reference = "";
  if (out["62"]) {
    const sub = out["62"];
    let j = 0;
    while (j + 4 <= sub.length) {
      const sid = sub.substr(j, 2);
      const slen = parseInt(sub.substr(j + 2, 2), 10);
      if (Number.isNaN(slen)) break;
      if (sid === "01" || sid === "05") reference = sub.substr(j + 4, slen);
      j += 4 + slen;
    }
  }
  const ibanMatch = t.match(IBAN_RE);
  return {
    scheme: "emvco",
    recipient: ibanMatch ? stripIban(ibanMatch[1]) : (out["59"] || "merchant"),
    recipient_name: out["59"] || null,
    amount: out["54"] ? parseFloat(out["54"]) : null,
    currency: normCcy(out["53"] || "EUR"),
    reference,
  };
};

const parseUrl = (t) => {
  const ib = t.match(IBAN_RE);
  let amount = null;
  let currency = "EUR";
  try {
    const u = new URL(t);
    const am = u.searchParams.get("amount") || u.searchParams.get("am");
    if (am) amount = parseFloat(am);
    const cu = u.searchParams.get("currency") || u.searchParams.get("cu");
    if (cu) currency = normCcy(cu);
  } catch (e) {}
  return {
    scheme: "url",
    recipient: ib ? stripIban(ib[1]) : t,
    recipient_name: null,
    amount,
    currency,
    reference: "",
  };
};

export const parseCredentials = (text) => {
  const t = clean(text);
  if (!t) return null;
  const upper = t.toUpperCase();
  let parsed;
  if (upper.startsWith("SIGNET-PAY")) parsed = parseSignet(t);
  else if (upper.startsWith("SIGNET-SEPA:")) parsed = { scheme: "signet", recipient: stripIban(t.split(":")[1]), recipient_name: null, amount: null, currency: null, reference: "" };
  else if (upper.startsWith("BCD")) parsed = parseEpc(t);
  else if (upper.startsWith("UPI://")) parsed = parseUpi(t);
  else if (upper.startsWith("000201")) parsed = parseEmvco(t);
  else if (/^https?:\/\//i.test(t)) parsed = parseUrl(t);
  else if (IBAN_RE.test(t)) parsed = { scheme: "iban", recipient: stripIban(t.match(IBAN_RE)[1]), recipient_name: null, amount: null, currency: null, reference: "" };
  else parsed = { scheme: "external", recipient: t, recipient_name: null, amount: null, currency: null, reference: "" };
  return { ...parsed, raw: t };
};

// Backwards-compatible alias (older imports)
export const parseQrPayload = parseCredentials;

// An IBAN-looking recipient can be checked against the SIGNET network.
export const looksLikeIban = (s) => IBAN_RE.test(clean(s));
